import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'

import { sendForgotPassword } from '../../../store/knowledge/actions'
import CopyIcon from '../../../components/UI/Common/CopyIcon'

type ForgotPasswordUser = {
  user_id: string
  show: boolean
  size?: 'sm' | 'lg' | 'xl'
  onHide: () => void
}

export default function ForgotPasswordModal(props: ForgotPasswordUser) {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [forgotPasswordUrl, setForgotPasswordUrl] = useState('')

  useEffect(() => {
    const getForgotPasswordUrl = async () => {
      const response: any = await dispatch(sendForgotPassword(props.user_id))
      if (response && response?.data?.reset_password_url) {
        setForgotPasswordUrl(response.data.reset_password_url)
      }
    }
    props.show && getForgotPasswordUrl()
  }, [props])

  const sendForgotPasswordEmail = async () => {
    await dispatch(sendForgotPassword(props.user_id, true))
    props.onHide()
  }

  return (
    <Modal
      open={props.show}
      onClose={props.onHide}
      sx={{ outline: 0 }}
      className="forgot-password-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: props.size === 'lg' ? '600px' : '500px',
          maxWidth: '95vw',
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          transform: 'translate(-50%, -50%)',
          borderRadius: '8px',
          overflow: 'hidden',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="subtitle2"
            component="h3"
            sx={{ fontWeight: 700 }}
          >
            {t('Forgot Password')}
          </Typography>
          <IconButton onClick={props.onHide} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ my: 1 }} />

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            py: 2,
            px: 2,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {t('Link to reset password')}
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              value={forgotPasswordUrl}
              disabled
              size="small"
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                },
              }}
            />
            <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CopyIcon text={forgotPasswordUrl} />
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="center" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            sx={{ flex: 1, textTransform: 'none' }}
            onClick={props.onHide}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            sx={{ flex: 1, textTransform: 'none' }}
            onClick={sendForgotPasswordEmail}
          >
            {t('Send Email')}
          </Button>
        </Stack>
      </Box>
    </Modal>
  )
}
